import React, { useEffect, useState } from "react";
import { Users } from "lucide-react";
import { apiGet } from "../../../Api/Client.js";
import LoadFailed from "./LoadFailed.jsx";

// Parameter x rep miss rates — the view an individual scorecard cannot produce.
//
// The whole point is the READ ACROSS A ROW. If five of six reps miss
// "pain into numbers", that is not five coaching problems. It is one playbook
// problem, and the action — fix the script, fix the training, or fix the rubric
// — is completely different from booking six one-to-ones.
//
// That read needs one stable rubric applied identically to a whole team, which
// is exactly what per-manager scorecard customisation destroys, and it is why
// the rubric lives in Django as a single source of truth rather than being
// configurable per viewer.
//
// Cells with no exposure render as blank, not as zero. A rep who has never been
// tested on something has not passed it, and a grid full of green nobody earned
// is worse than a grid with holes in it.

const muted = { color: "var(--c-500)" };
const card = { background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 14, padding: "16px 18px" };

// Column headers name a person, so they have to stay a name. Taking the first
// word turned "Local Demo Rep" into "Local", which reads as a place or a
// truncation bug rather than as somebody. Full names while the team is small
// enough to fit them; "First L." once it isn't.
function columnName(name, teamSize) {
  const text = (name || "").trim();
  if (!text) return "";
  if (teamSize <= 4) return text;
  const words = text.split(/\s+/);
  if (words.length === 1) return words[0];
  return `${words[0]} ${words[words.length - 1][0]}.`;
}

function cellColour(rate) {
  if (rate == null) return "transparent";
  if (rate >= 0.6) return "rgba(192, 57, 43, 0.55)";
  if (rate >= 0.35) return "rgba(192, 57, 43, 0.28)";
  if (rate > 0) return "rgba(192, 57, 43, 0.12)";
  return "rgba(40, 180, 120, 0.18)";
}

export default function TeamHeatmap() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [windowDays, setWindowDays] = useState(30);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // A new window (or a retry) starts clean: no old figures under the new window, no old error.
    setData(null);
    setError("");
    apiGet(`/RevOps/team-patterns/?window=${windowDays}`)
      .then((payload) => !cancelled && setData(payload))
      .catch((err) => !cancelled && setError(err?.message || "Couldn't load the team view."));
    return () => { cancelled = true; };
  }, [windowDays, attempt]);

  if (error) return <LoadFailed message={error} onRetry={() => setAttempt((n) => n + 1)} />;
  if (!data) return <p style={muted}>Loading…</p>;

  const rows = data.rows || [];
  // Only parameters somebody on the team has actually been scored on. Showing
  // all sixteen would bury the signal in empty columns.
  const params = (data.parameters || []).filter((p) =>
    rows.some((r) => (r.cells[p.key] || {}).opportunities),
  );

  if (!rows.length || !params.length) {
    return (
      <div style={card}>
        <p style={{ ...muted, fontSize: 12.5, margin: 0 }}>
          No scored calls in this window yet — the heatmap fills in as calls come through.
        </p>
      </div>
    );
  }

  // The playbook-gap read is a comparison ACROSS reps, so it needs at least two
  // of them tested on the same parameter. Below that the server correctly
  // returns no gaps — and an empty space where the headline feature should be
  // reads as "nothing wrong" or as a broken panel. Say which it is.
  const repsWithExposure = rows.filter((r) =>
    params.some((p) => (r.cells[p.key] || {}).opportunities),
  ).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {repsWithExposure < 2 && (
        <div style={{ ...card, borderStyle: "dashed" }}>
          <strong style={{ fontSize: 13, color: "var(--c-900)" }}>
            Not enough reps yet to tell a playbook gap from a coaching one
          </strong>
          <p style={{ margin: "5px 0 0", fontSize: 12.5, color: "var(--c-700)" }}>
            {repsWithExposure === 1
              ? "One rep has scored calls in this window."
              : "No rep has scored calls in this window."}{" "}
            The read this page exists for — five of six reps missing the same thing is one
            script problem, not five coaching problems — needs at least two people tested on
            the same parameter. The grid below is still accurate; it just can't make that
            call yet.
          </p>
        </div>
      )}

      {data.playbook_gaps?.length > 0 && (
        // Surfaced above the grid rather than left for someone to spot: the
        // whole-row-red read is the highest-value thing on this page and it is
        // easy to miss inside a table.
        <div style={{ ...card, borderColor: "var(--red-600, #c0392b)" }}>
          <strong style={{ fontSize: 13, color: "var(--c-900)" }}>Looks like a playbook gap, not a coaching gap</strong>
          {data.playbook_gaps.map((g) => (
            <p key={g.parameter_key} style={{ margin: "5px 0 0", fontSize: 12.5, color: "var(--c-700)" }}>
              <strong>{g.label}</strong> — {g.reps_failing} of {g.reps_with_exposure} reps who were tested on it
              are missing it. That's the script or the training, not {g.reps_failing} separate
              conversation{g.reps_failing === 1 ? "" : "s"}.
            </p>
          ))}
        </div>
      )}

      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5,
                         fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
                         color: "var(--c-600)" }}>
            <Users size={13} /> Team patterns
          </span>
          <span style={{ display: "flex", gap: 4 }}>
            {[7, 14, 30].map((d) => (
              <button key={d} onClick={() => setWindowDays(d)} style={{
                border: "none", background: "transparent", cursor: "pointer", fontSize: 11,
                padding: "2px 6px", borderRadius: 6,
                fontWeight: windowDays === d ? 700 : 400,
                color: windowDays === d ? "var(--blue-600)" : "var(--c-500)",
              }}>{d}d</button>
            ))}
          </span>
        </div>

        <div style={{ overflowX: "auto", marginTop: 10 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 11.5, minWidth: "100%" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "4px 8px 6px 0", ...muted, fontWeight: 600 }}>Parameter</th>
                {rows.map((r) => (
                  <th key={r.employee_id} title={r.name || ""}
                      style={{ padding: "4px 10px 6px", ...muted, fontWeight: 600,
                               whiteSpace: "nowrap", textAlign: "center", width: 1 }}>
                    {columnName(r.name, rows.length) || r.employee_id}
                  </th>
                ))}
                {/* A slack column. Without it `minWidth: 100%` divided the
                    table's width between the columns that exist, so a team of
                    one produced a single cell stretched across the page — a
                    layout that looks broken and isn't. The spacer absorbs the
                    surplus instead. */}
                <th aria-hidden="true" style={{ width: "100%" }} />
              </tr>
            </thead>
            <tbody>
              {params.map((p) => (
                <tr key={p.key}>
                  <td style={{ padding: "4px 8px 4px 0", color: "var(--c-800)", whiteSpace: "nowrap" }}>
                    {p.label}
                  </td>
                  {rows.map((r) => {
                    const cell = r.cells[p.key];
                    const rate = cell?.rate;
                    return (
                      <td key={r.employee_id}
                          title={cell?.opportunities
                            ? `${cell.misses} of ${cell.opportunities} calls`
                            : "not scored on this yet"}
                          style={{
                            padding: "4px 6px", textAlign: "center", background: cellColour(rate),
                            border: "1px solid var(--c-200)", fontVariantNumeric: "tabular-nums",
                            color: rate == null ? "var(--c-400)" : "var(--c-900)",
                          }}>
                        {/* the denominator rides along even here, in the title —
                            2 of 2 and 12 of 20 are not the same red */}
                        {cell?.opportunities ? `${cell.misses}/${cell.opportunities}` : "·"}
                      </td>
                    );
                  })}
                  <td aria-hidden="true" />
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p style={{ margin: "8px 0 0", fontSize: 11, ...muted }}>
          Each cell is misses over calls where that parameter was actually observed. A blank means
          it hasn't come up on that rep's calls — not that they passed.
        </p>
      </div>
    </div>
  );
}
